/**
 * Parameter LLM global yang bisa diatur MANUAL dari dashboard (tab ⚙️ LLM) —
 * SATU sumber kebenaran untuk spec (label, batas, nilai bawaan), normalisasi,
 * dan urutan prioritas pemakaian.
 *
 * Kenapa dipisah dari provider: kolom `aiGatewayProviders.temperature/maxTokens/
 * timeoutMs` = setelan seed per provider (mis. 9router 0.1/500/15000). Parameter
 * di sini = knob GLOBAL superadmin; begitu diisi, nilainya menimpa setelan
 * provider — jadi cukup satu tempat untuk mengganti-ganti nilai tanpa
 * menyentuh provider satu per satu.
 *
 * Prioritas (yang pertama ada & valid dipakai):
 *   1. argumen request (`chat({ temperature, maxTokens })`) — pemanggil kode
 *   2. parameter global di doc `aiGatewaySettings` (diisi dari dashboard)
 *   3. kolom provider (`temperature` / `maxTokens` / `timeoutMs`)
 *   4. nilai bawaan kode di `fallback` spec (`LLM_PARAM_DEFAULTS`)
 *
 * Catatan jalur BOT (`botLlm.planMessage`, 10 Okt 2026): bot mengirim argumen
 * eksplisit — `temperature` hasil `lib/chatStyle.resolveMessageTemperature`
 * (aksi 0.2 / obrolan dari `chatTemperature`) dan `maxTokens` dari parameter
 * global — sehingga setelan tab ⚙️ LLM benar-benar berlaku untuk bot (dulu
 * `temperature: 0.1`/`maxTokens: 700` dipaku di kode dan menimpa dashboard).
 *
 * Selama field di DB belum diisi, hasilnya SAMA dengan perilaku sebelumnya
 * (provider → bawaan kode) — fitur ini tidak mengubah perilaku sampai
 * superadmin benar-benar mengubah angkanya.
 *
 * Dipakai di:
 *   - convex/aiGateway.ts    → resolusi nilai efektif saat request
 *   - convex/dashboard.ts    → getLlmSettings / setLlmParam (validasi + pesan)
 *   - dashboard/src/App.tsx  → tab ⚙️ LLM (input manual, nilai bawaan tampil)
 *   - scripts/probe-llm-params.ts → anti-regresi
 */

export const LLM_PARAM_SPECS = [
  {
    key: "temperature",
    field: "llmTemperature",
    label: "Temperature",
    unit: "",
    step: 0.1,
    min: 0,
    max: 2,
    integer: false,
    fallback: 0.4,
    hint: "Kreativitas jawaban (0 = kaku/pasti, 2 = liar). Dipakai saat pemanggil tidak mengirim suhu sendiri.",
  },
  {
    key: "chatTemperature",
    field: "llmChatTemperature",
    label: "Temperature obrolan",
    unit: "",
    step: 0.1,
    min: 0,
    max: 1.2,
    integer: false,
    fallback: 0.8,
    hint:
      "Kreativitas balasan OBROLAN saja (sapaan, pertanyaan ringan). 0 = kaku & berulang, 0.8 = variatif tapi tetap sopan. Jalur catat transaksi TIDAK memakai nilai ini — di kode ia tetap suhu rendah (lib/chatStyle) supaya rencana aksi stabil.",
  },
  {
    key: "maxTokens",
    field: "llmMaxTokens",
    label: "Maks token",
    unit: "token",
    step: 64,
    min: 64,
    max: 8192,
    integer: true,
    fallback: 800,
    hint: "Batas panjang jawaban model. Terlalu kecil → JSON plan terpotong (bot balas \"Format AI tidak valid\").",
  },
  {
    key: "timeoutMs",
    field: "llmTimeoutMs",
    label: "Timeout",
    unit: "ms",
    step: 1000,
    min: 1000,
    max: 120000,
    integer: true,
    fallback: 30000,
    hint: "Batas tunggu satu request ke provider sebelum dianggap gagal dan rotasi pindah ke model berikutnya.",
  },
  {
    key: "maxKeyAttempts",
    field: "llmMaxKeyAttempts",
    label: "Maks percobaan key",
    unit: "key",
    step: 1,
    min: 1,
    max: 10,
    integer: true,
    fallback: 3,
    hint: "Jumlah key satu provider yang dicoba (LRU) sebelum rotasi pindah ke entri chain berikutnya.",
  },
] as const;

export type LlmParamSpec = (typeof LLM_PARAM_SPECS)[number];
export type LlmParamKey = LlmParamSpec["key"];
export type LlmParamField = LlmParamSpec["field"];

/** Parameter LLM yang tersimpan di DB (hanya yang sudah diisi manual). */
export type LlmParams = Partial<Record<LlmParamKey, number>>;

/** Nilai bawaan kode per parameter (dipakai bila semua sumber lain kosong). */
export function llmParamDefaults(): Record<LlmParamKey, number> {
  const out = {} as Record<LlmParamKey, number>;
  for (const spec of LLM_PARAM_SPECS) out[spec.key] = spec.fallback;
  return out;
}

/** Spec satu parameter; `null` bila key tidak dikenal (arg dari luar). */
export function llmParamSpec(key: string): LlmParamSpec | null {
  return LLM_PARAM_SPECS.find((spec) => spec.key === key) ?? null;
}

/**
 * Anggaran token percobaan BERIKUTNYA saat model memotong output di tengah
 * (16 Okt 2026 — akar "ada LLM yang tidak bisa parse JSON").
 *
 * Akar masalah: model yang output-nya kena `max_tokens` membalas JSON yang
 * tidak seimbang (`finish_reason = "length"`) → parser mengembalikan null →
 * sistem menuduh "model tidak bisa JSON" dan berpindah model. Padahal model itu
 * SEHAT; yang kurang cuma anggaran token. Fungsi ini menggandakan batas untuk
 * percobaan ulang, dibatasi `max` spec (`llmParamSpec("maxTokens")` — satu
 * sumber angka, tidak ada konstanta kembar di pemanggil).
 *
 * `null` = sudah mentok batas atas → TIDAK ada gunanya mengulang model yang
 * sama; pemanggil baru boleh memperlakukan sebagai kegagalan model.
 */
export function escalateMaxTokens(current: number): number | null {
  const spec = llmParamSpec("maxTokens");
  if (!spec) return null;
  // Nilai luar rentang (mis. angka lama tersimpan sebelum spec berubah)
  // diklamp dulu supaya eskalasi tetap masuk akal — bukan 2× nilai liar.
  const raw = Number.isFinite(current) ? current : spec.fallback;
  const base = Math.min(spec.max, Math.max(spec.min, raw));
  if (base >= spec.max) return null;
  const target = spec.integer ? Math.ceil((base * 2) / spec.step) * spec.step : base * 2;
  return Math.min(spec.max, Math.max(base + spec.step, target));
}

/**
 * Batasi nilai ke rentang spec + bulatkan bila spec.integer. `null` = nilai
 * tidak valid (bukan angka finite) atau di luar rentang keras `hardMin`/`hardMax`
 * — dipakai mutation supaya input dashboard tidak diam-diam dibulatkan ke nilai
 * lain tanpa pemberitahuan.
 */
export function normalizeLlmParam(
  key: LlmParamKey,
  raw: unknown,
  opts: { clamp?: boolean } = {}
): number | null {
  const spec = llmParamSpec(key);
  if (!spec) return null;
  // `null` / `undefined` / string kosong = "belum diisi", BUKAN 0 — kalau tidak,
  // temperature 0 valid tapi input kosong diam-diam jadi 0.
  const value =
    typeof raw === "number"
      ? raw
      : typeof raw === "string" && raw.trim() !== ""
        ? Number(raw)
        : Number.NaN;
  if (!Number.isFinite(value)) return null;
  if (value < spec.min || value > spec.max) {
    if (opts.clamp !== true) return null;
  }
  const bounded = Math.min(spec.max, Math.max(spec.min, value));
  return spec.integer ? Math.trunc(bounded) : bounded;
}

/** Pesan error Indonesia (tanpa istilah teknis) untuk input di luar batas. */
export function llmParamError(key: LlmParamKey): string {
  const spec = llmParamSpec(key);
  if (!spec) return "Parameter LLM tidak dikenal";
  const unit = spec.unit ? ` ${spec.unit}` : "";
  return `${spec.label} harus angka ${spec.min}-${spec.max}${unit}.`;
}

/**
 * Baca parameter yang SUDAH diisi manual dari doc `aiGatewaySettings`. Nilai
 * rusak / di luar batas diabaikan (dianggap belum diisi) supaya DB tidak bisa
 * mematikan runtime chat.
 */
export function readLlmParams(settings: unknown): LlmParams {
  const source = (settings ?? {}) as Record<string, unknown>;
  const out: LlmParams = {};
  for (const spec of LLM_PARAM_SPECS) {
    const value = normalizeLlmParam(spec.key, source[spec.field], { clamp: true });
    if (value !== null) out[spec.key] = value;
  }
  return out;
}

/**
 * Nilai efektif satu parameter: request → global (dashboard) → provider →
 * bawaan kode. Nilai provider di-clamp ke rentang spec supaya kolom DB yang
 * aneh (mis. temperature 5) tidak lolos ke provider LLM.
 */
export function resolveLlmParam(
  key: LlmParamKey,
  sources: { request?: unknown; stored?: unknown; provider?: unknown } = {}
): number {
  const spec = llmParamSpec(key);
  if (!spec) throw new Error(`Parameter LLM tidak dikenal: ${key}`);
  for (const raw of [sources.request, sources.stored, sources.provider]) {
    const value = normalizeLlmParam(key, raw, { clamp: true });
    if (value !== null) return value;
  }
  return spec.fallback;
}
